'use client';

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useOverseasRestricted } from '@/hooks/useRestrictedMode';
import { NetworkManager, request, axiosMethod } from '@/api/http';
import { trackCompleteRegistration } from '@/utils/metaPixel';
import { trackGtmSignup } from '@/utils/gtm';
import { trackMobonSignup } from '@/utils/mobon';
import { track } from '@/utils/analytics';
import { claimCreatorReferral } from '@/utils/creatorReferral';
import OnboardingOverlay from './OnboardingOverlay';
import WelcomeFlow from './WelcomeFlow';

type Phase = 'idle' | 'onboarding' | 'welcome-flow' | 'done';

const instance = new NetworkManager('rizzling').getInstance();

interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

/**
 * 온보딩 게이트
 *
 * - 로그인 + 온보딩 미완료 → 온보딩 오버레이 → (신규) 가입축하 모달
 * - 로그인 + 온보딩 완료 → 아무것도 안 함 (출석은 설정-출석체크 페이지에서 받음)
 * - 비로그인 → 아무것도 안 함
 */
export default function OnboardingGate() {
  const user = useAuthStore((s) => s.user);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isOverseasRestricted = useOverseasRestricted();
  const [phase, setPhase] = useState<Phase>('idle');
  const qc = useQueryClient();

  useEffect(() => {
    if (!isLoggedIn || !user) {
      setPhase('idle');
      return;
    }

    // 심사용 테스트 계정 또는 해외 Apple 앱 유저는 가입축하 모달 스킵
    if (user.isTestAccount || isOverseasRestricted) {
      setPhase('done');
      return;
    }

    if (!user.isOnboardingCompleted) {
      setPhase('onboarding');
      track('onboarding_start');
    } else {
      setPhase('done');
    }
  }, [isLoggedIn, user, isOverseasRestricted]);

  interface OnboardingResponseData {
    reward?: { type?: string; amount?: number };
  }

  /** reward.amount === 0 이면 탈퇴 후 재가입한 복귀 유저 → 가입축하 모달 스킵 */
  const nextPhaseFromResponse = (data: OnboardingResponseData | undefined): Phase =>
    data?.reward?.amount === 0 ? 'done' : 'welcome-flow';

  /** 온보딩 완료 직후 — 보상 다이아/아이템/출석 상태 모두 invalidate. 캐시된 0 잔액이 즉시 갱신되도록. */
  const invalidatePostSignupCaches = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['balance'] });
    qc.invalidateQueries({ queryKey: ['inventory'] });
    qc.invalidateQueries({ queryKey: ['chat-list'] });
    qc.invalidateQueries({ queryKey: ['chat-room-items'] });
    qc.invalidateQueries({ queryKey: ['attendance-status'] });
    qc.invalidateQueries({ queryKey: ['attendance'] });
  }, [qc]);

  const handleOnboardingComplete = useCallback(async (
    name: string,
    birthDate?: string,
    referralCode?: string,
    isMarketingAgreed?: boolean,
  ) => {
    try {
      const res = await request<ApiResponse<OnboardingResponseData>>(
        instance,
        axiosMethod.POST,
        '/auth/onboarding',
        undefined,
        {
          name,
          ...(birthDate ? { birthDate } : {}),
          ...(referralCode ? { referralCode } : {}),
          isMarketingAgreed: !!isMarketingAgreed,
        },
      );
      await useAuthStore.getState().fetchMe();
      invalidatePostSignupCaches();
      const updatedUser = useAuthStore.getState().user;
      if (updatedUser) {
        trackCompleteRegistration(updatedUser.id, updatedUser.socialProvider);
        trackGtmSignup();
        trackMobonSignup({
          memberId: updatedUser.id,
          provider: updatedUser.socialProvider,
          memberName: updatedUser.name,
          email: updatedUser.email,
        });
        track('signup_complete', { provider: updatedUser.socialProvider });
        track('onboarding_complete');
        claimCreatorReferral();
      }
      setPhase(nextPhaseFromResponse(res.data.data));
    } catch (err) {
      console.error('[OnboardingGate] 온보딩 완료 실패:', err);
    }
  }, [invalidatePostSignupCaches]);

  const handleOnboardingSkip = useCallback(async () => {
    // 건너뛰기: 기본 이름으로 온보딩 완료 처리
    try {
      const res = await request<ApiResponse<OnboardingResponseData>>(
        instance,
        axiosMethod.POST,
        '/auth/onboarding',
        undefined,
        { name: '뮤즈링 유저', isMarketingAgreed: false },
      );
      await useAuthStore.getState().fetchMe();
      invalidatePostSignupCaches();
      const updatedUser = useAuthStore.getState().user;
      if (updatedUser) {
        trackCompleteRegistration(updatedUser.id, updatedUser.socialProvider);
        trackGtmSignup();
        trackMobonSignup({
          memberId: updatedUser.id,
          provider: updatedUser.socialProvider,
          memberName: updatedUser.name,
          email: updatedUser.email,
        });
        track('signup_complete', { provider: updatedUser.socialProvider });
        track('onboarding_complete', { skipped: true });
        claimCreatorReferral();
      }
      setPhase(nextPhaseFromResponse(res.data.data));
    } catch (err) {
      console.error('[OnboardingGate] 온보딩 건너뛰기 실패:', err);
    }
  }, [invalidatePostSignupCaches]);

  const handleFlowDone = useCallback(() => {
    setPhase('done');
  }, []);

  if (phase === 'idle' || phase === 'done') return null;

  return (
    <>
      {phase === 'onboarding' && (
        <OnboardingOverlay
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
      {phase === 'welcome-flow' && (
        <WelcomeFlow showWelcome={true} onDone={handleFlowDone} />
      )}
    </>
  );
}
